import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Select,
  Space,
  TableProps,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../../assets/icons/ViewIcon";
import StatusItem from "../../../../../components/common/StatusItem";
import CustomModal from "../../../../../components/ui/CustomModal";
import CustomTable from "../../../../../components/ui/CustomTable";
import { dbDateFormat } from "../../../../../constants/constants";
import {
  useGetAllPayrollBatchQuery,
  useGetCompanyWalletQuery,
  useSentToDisbursementMutation,
} from "../../../../../features/organization/contractual/Payroll/payrollApi";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import useTable from "../../../../../hooks/useTable";
import { formatDate } from "../../../../../lib/time";
import { toast } from "../../../../../lib/toast";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../../utils/string";
import { addKeyInArray, selectArrayOption } from "../../../../../utils/utils";
import CalanderIcon from "../../../survey/templates/_icons/CalanderIcon";
const { RangePicker } = DatePicker;

const STATUSES = [
  {
    label: "Pending",
    value: "pending",
    className: "status-warning rounded-lg",
  },
  {
    label: "Processing",
    value: "processing",
    className: "status-link rounded-lg",
  },
  {
    label: "Completed",
    value: "completed",
    className: "status-success rounded-lg",
  },
  {
    label: "Failed",
    value: "failed",
    className: "status-danger rounded-lg",
  },
];

const PayrollBatches = () => {
  const [form] = Form.useForm();
  const { open, handleOpen, handleClose } = useModal();
  const [selectedData, setSelectedData] = useState<(string | number)[]>([]);

  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useTable<any>();

  const { data, isLoading, isFetching } = useGetAllPayrollBatchQuery({
    params: { ...filter, search: keyword },
  });

  const { data: wallets } = useGetCompanyWalletQuery({});

  const [
    sentToDisbursement,
    { isError, error, isLoading: senting, isSuccess },
  ] = useSentToDisbursementMutation();

  const handleSentToDisbursement = (values: any) => {
    if (!selectedData?.length) return null;
    sentToDisbursement({ payroll_batch_ids: selectedData, ...values });
  };

  useError(isError, error, "Sent to disbursement failed.");

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Successfully batch send to disbursement");
      handleClose();
      form.resetFields();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, form]);

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      align: "center",
      title: "Cycle",
      dataIndex: "cycle",
      render: (val) => valueOrNa(val),
    },
    {
      align: "center",
      title: "Payment Type",
      dataIndex: "payment_type",
      render: (val) => (val ? capitalize(val) : "n/a"),
    },
    {
      align: "center",
      title: "Payment Period",
      dataIndex: "payment_period",
      render: (record) => (
        <>
          {formatDate(record?.period_from, "DD MMM")}-
          {formatDate(record?.period_to)}
        </>
      ),
    },
    {
      align: "center",
      title: "Employees",
      dataIndex: "employee_count",
      render: (val) => valueOrNa(val),
    },
    {
      align: "center",
      title: "Total Payout",
      dataIndex: "total_payout",
      render: (val) => `৳${addCommaInNumber(val || 0)}`,
    },
    {
      align: "center",
      title: "Status",
      dataIndex: "status",
      render: (val) => <StatusItem value={val} list={STATUSES} />,
    },
    {
      title: "Action",
      align: "center",
      render: (record) => (
        <Space align="center">
          <Link
            to={`payroll-details/${record?.id}?cycle=${record?.cycle}&status=${record?.status}`}
          >
            <Tooltip title="View">
              <Button
                className="action-view"
                size="small"
                icon={<ViewIcon />}
              />
            </Tooltip>
          </Link>
        </Space>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        page={page}
        limit={limit}
        columns={columns}
        total={data?.data?.total}
        handleLimit={handleLimit}
        loading={isLoading || isFetching}
        handlePagination={handlePagination}
        dataSource={addKeyInArray(data?.data?.batches)}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as string[]);
          },
          getCheckboxProps: (record) => {
            return {
              disabled: record?.status !== "pending",
            };
          },
          columnWidth: 40,
        }}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />

            {/* <Select
              allowClear
              placement="bottomRight"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              placeholder="Payment Type"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              onChange={(value) => handleFilter("payment_type", value)}
              options={[
                { label: "Daily", value: "daily" },
                { label: "Weekly", value: "weekly" },
                { label: "Monthly", value: "monthly" },
              ]}
            /> */}
            <RangePicker
              style={{ width: 245 }}
              separator="-"
              className="w-full input-bordered"
              format={"DD/MM/YYYY"}
              suffixIcon={<CalanderIcon />}
              placeholder={["dd/mm/yyyy", "dd/mm/yyyy"]}
              onChange={(dates: (Dayjs | null)[] | null) => {
                const [start, end] = dates || [];

                // Call filter only if both dates are selected
                if (start && end) {
                  handleFilter(
                    "period_from",
                    dayjs(start).format(dbDateFormat)
                  );
                  handleFilter("period_to", dayjs(end).format(dbDateFormat));
                } else {
                  // Clear filters if user clears dates
                  handleFilter("period_from", undefined);
                  handleFilter("period_to", undefined);
                }
              }}
            />

            <Select
              allowClear
              placeholder="Status"
              className="min-w-[90px]"
              placement="bottomRight"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              onChange={(value) => handleFilter("status", value)}
              options={selectArrayOption(STATUSES, "label", "value")}
            />

            <Button
              type="primary"
              color="primary"
              loading={senting}
              disabled={!selectedData?.length}
              onClick={() => handleOpen({ type: "VIEW" })}
            >
              Send to Disbursement
            </Button>
          </>
        }
      />

      <CustomModal
        footer={null}
        width={350}
        open={open.open}
        handleClose={() => {
          handleClose();
          form.resetFields();
        }}
        title="Select Wallet"
      >
        <Form
          form={form}
          size="large"
          layout="vertical"
          requiredMark="optional"
          className="custom-form"
          onFinish={handleSentToDisbursement}
        >
          <Form.Item
            label="Select Wallet"
            name={"wallet_number"}
            rules={[{ required: true, message: "Please select wallet" }]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={(wallets?.data?.data?.wallets || []).map((item) => ({
                label: `${item.wallet_name} - ${item.wallet_number}`,
                value: item.wallet_number,
              }))}
              placeholder="Select"
            />
          </Form.Item>
          <Flex align="center" justify="end" gap={16}>
            <Button
              onClick={() => {
                handleClose();
                form.resetFields();
              }}
              htmlType="button"
              size="middle"
              className="h-10"
            >
              Cancel
            </Button>
            <Button
              loading={senting}
              htmlType="submit"
              size="middle"
              className="h-10"
              type="primary"
            >
              Continue
            </Button>
          </Flex>
        </Form>
      </CustomModal>
    </>
  );
};

export default PayrollBatches;
